import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { webPageService } from '@/lib/api/services/web-pages'
import { ApiResponse, PaginatedResponse, PaginatedWebPage, PaginationParams, UpdateAllWebPageSections, WebsitePageVersionsResponse, WebsitePageContentResponse, WebPagesStats, WebPagesStatsResponse, WebPageVersionsStats, WebPageVersionsStatsResponse } from '@/lib/api/types'
import { WebsitePageSection } from '@/types';

// Query keys for web pages management
export const webPagesKeys = {
  all: ['web-pages'] as const,
  allWebPages: () => [...webPagesKeys.all, 'allWebPages'] as const,
  webPage: (id: string) => [...webPagesKeys.allWebPages(), id] as const,
  webPagesPaginated: (params?: PaginationParams) =>
    [...webPagesKeys.allWebPages(), 'paginated', params] as const,
  webPagesAll: () => [...webPagesKeys.allWebPages(), 'all'] as const,
  webPagesSearch: (query: string) => [...webPagesKeys.allWebPages(), 'search', query] as const,
  stats: () => [...webPagesKeys.all, 'stats'] as const,
  versions: (id: string, params?: PaginationParams) => [...webPagesKeys.webPage(id), 'versions', params] as const,
  content: (contentId: string) => [...webPagesKeys.all, 'content', contentId] as const,
  versionsStats: (id: string) => [...webPagesKeys.webPage(id), 'versions', 'stats'] as const
}

/**
 * Get web pages with pagination and filtering
 */
export const usePaginatedWebPages = (params: PaginationParams = { page: 1, size: 20 }) => {
  return useQuery<ApiResponse<PaginatedResponse<PaginatedWebPage>>, Error>({
    queryKey: webPagesKeys.webPagesPaginated(params),
    queryFn: () => webPageService.getPaginatedWebPages(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData
  })
}

export const useWebPagesStats = () => {
  return useQuery<WebPagesStatsResponse, Error, WebPagesStats>({
    queryKey: webPagesKeys.stats(),
    queryFn: () => webPageService.getWebPagesStats(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData
  })
}

export const useWebPageVersions = (id: string, params: PaginationParams = { page: 1, size: 20 }) => {
  return useQuery<WebsitePageVersionsResponse, Error>({
    queryKey: webPagesKeys.versions(id, params),
    queryFn: () => webPageService.getWebPageVersions(id, params),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData
  })
}

export const useWebPageContent = (contentId: string) => {
  return useQuery<WebsitePageContentResponse, Error>({
    queryKey: webPagesKeys.content(contentId),
    queryFn: () => webPageService.getWebPageContent(contentId),
    enabled: !!contentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData
  })
}

export const useWebPageVersionsStats = (id: string) => {
  return useQuery<WebPageVersionsStatsResponse, Error, WebPageVersionsStats>({
    queryKey: webPagesKeys.versionsStats(id),
    queryFn: () => webPageService.getWebPageVersionStats(id),
    enabled: !!id,
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData
  })
}


/**
 * Update all web page sections mutation
 */
export const useUpdateAllWebPageSections = () => {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Record<string, unknown>>, Error, {
    contentId: string;
    data: UpdateAllWebPageSections
  }>({
    mutationFn: ({
      contentId,
      data
    }) =>
      webPageService.updateAllWebPageSections(contentId, data),
    onSuccess: (_, { contentId }) => {
      // Invalidate and refetch queries
      queryClient.invalidateQueries({
        queryKey: webPagesKeys.content(contentId),
        refetchType: 'active'
      })
      queryClient.invalidateQueries({
        queryKey: webPagesKeys.webPagesPaginated(),
        refetchType: 'active'
      })
    },
    onError: (error) => {
      console.error('Web page sections update failed:', error)
    }
  })
}

/**
 * Update web page status mutation
 */
export const useUpdateWebPageStatus = () => {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Record<string, unknown>>, Error, {
    contentId: string;
    status: 'DRAFT' | 'PUBLISH'
  }>({
    mutationFn: ({
      contentId,
      status
    }) =>
      webPageService.updateWebPageStatus(contentId, status),
    onSuccess: (_, { contentId }) => {
      // Invalidate and refetch queries
      queryClient.invalidateQueries({
        queryKey: webPagesKeys.content(contentId),
        refetchType: 'active'
      })
      queryClient.invalidateQueries({
        queryKey: webPagesKeys.webPagesPaginated(),
        refetchType: 'active'
      })
    },
    onError: (error) => {
      console.error('Web page status update failed:', error)
    }
  })
}

/**
 * Delete web page content mutation
 */
export const useDeleteWebPageContent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (contentId: string) =>
      webPageService.deleteWebPageContent(contentId),
    onSuccess: (_, contentId) => {
      // Invalidate and refetch queries
      queryClient.invalidateQueries({
        queryKey: webPagesKeys.content(contentId),
        refetchType: 'active'
      })
      queryClient.invalidateQueries({
        queryKey: webPagesKeys.allWebPages(),
        refetchType: 'active'
      })
    },
    onError: (error) => {
      console.error('Web page content deletion failed:', error)
    }
  })
}

/**
 * Create web page content version mutation
 */
export const useCreateWebPageVersion = () => {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Record<string, unknown>>, Error, {
    websitePageId: string;
    data: { status: 'DRAFT' | 'PUBLISH'; contents: WebsitePageSection[] }
  }>({
    mutationFn: ({
      websitePageId,
      data
    }) =>
      webPageService.createWebPageVersion(websitePageId, data),
    onSuccess: (_, { websitePageId }) => {
      // Invalidate and refetch queries
      queryClient.invalidateQueries({
        queryKey: webPagesKeys.webPage(websitePageId),
        refetchType: 'active'
      })
      queryClient.invalidateQueries({
        queryKey: webPagesKeys.allWebPages(),
        refetchType: 'active'
      })
    },
    onError: (error) => {
      console.error('Web page content version creation failed:', error)
    }
  })
}
